import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

export default function DailyRecord() {
  const navigate = useNavigate()
  const today = new Date().toISOString().split('T')[0]

  const [formData, setFormData] = useState({
    date: today,
    overallCondition: '',
    bowelMovements: '',
    stoolType: '',
    abdominalPain: 'none',
    appetite: '',
    sleep: '',
    mood: '',
    notes: '',
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Daily record submitted:', formData)
    // TODO: API送信
    navigate('/patient')
  }

  return (
    <div className="daily-record-page">
      <header className="page-header">
        <Link to="/patient" className="back-button">
          <span className="material-symbols-outlined">arrow_back</span>
        </Link>
        <h1 className="page-title">今日の記録</h1>
        <span className="page-date">{today}</span>
      </header>

      <form onSubmit={handleSubmit} className="record-form">
        {/* 全体的な体調 */}
        <section className="form-section">
          <h2 className="section-title">
            <span className="material-symbols-outlined">favorite</span>
            全体的な体調
          </h2>
          <div className="condition-buttons">
            {[
              { value: 'excellent', label: 'とても良い', emoji: '😊' },
              { value: 'good', label: '良い', emoji: '🙂' },
              { value: 'normal', label: '普通', emoji: '😐' },
              { value: 'poor', label: '悪い', emoji: '😞' },
              { value: 'very_poor', label: 'とても悪い', emoji: '😢' },
            ].map((option) => (
              <label
                key={option.value}
                className={`condition-button ${formData.overallCondition === option.value ? 'condition-button--selected' : ''}`}
              >
                <input
                  type="radio"
                  name="overallCondition"
                  value={option.value}
                  checked={formData.overallCondition === option.value}
                  onChange={handleChange}
                />
                <span className="condition-emoji">{option.emoji}</span>
                <span className="condition-label">{option.label}</span>
              </label>
            ))}
          </div>
        </section>

        {/* 便通 */}
        <section className="form-section">
          <h2 className="section-title">
            <span className="material-symbols-outlined">water_drop</span>
            便通について
          </h2>
          <div className="form-row">
            <div className="form-field">
              <label className="field-label">排便回数</label>
              <select
                name="bowelMovements"
                value={formData.bowelMovements}
                onChange={handleChange}
                className="field-select"
              >
                <option value="">選択してください</option>
                <option value="0">0回</option>
                <option value="1">1回</option>
                <option value="2">2回</option>
                <option value="3">3回</option>
                <option value="4">4回</option>
                <option value="5">5回以上</option>
              </select>
            </div>

            <div className="form-field">
              <label className="field-label">便の形状</label>
              <select
                name="stoolType"
                value={formData.stoolType}
                onChange={handleChange}
                className="field-select"
              >
                <option value="">選択してください</option>
                <option value="hard">硬い（コロコロ）</option>
                <option value="normal_hard">やや硬い</option>
                <option value="normal">普通</option>
                <option value="soft">やや軟らかい</option>
                <option value="loose">軟便</option>
                <option value="watery">水様便</option>
              </select>
            </div>
          </div>

          <div className="form-field">
            <label className="field-label">腹痛</label>
            <div className="radio-group">
              {[
                { value: 'none', label: 'なし' },
                { value: 'mild', label: '軽度' },
                { value: 'moderate', label: '中程度' },
                { value: 'severe', label: '強い' },
              ].map((option) => (
                <label key={option.value} className="radio-label">
                  <input
                    type="radio"
                    name="abdominalPain"
                    value={option.value}
                    checked={formData.abdominalPain === option.value}
                    onChange={handleChange}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </section>

        {/* 生活状況 */}
        <section className="form-section">
          <h2 className="section-title">
            <span className="material-symbols-outlined">self_improvement</span>
            生活状況
          </h2>
          <div className="form-row">
            <div className="form-field">
              <label className="field-label">食欲</label>
              <select
                name="appetite"
                value={formData.appetite}
                onChange={handleChange}
                className="field-select"
              >
                <option value="">選択してください</option>
                <option value="excellent">とても良い</option>
                <option value="good">良い</option>
                <option value="normal">普通</option>
                <option value="poor">悪い</option>
                <option value="none">食欲なし</option>
              </select>
            </div>

            <div className="form-field">
              <label className="field-label">睡眠</label>
              <select
                name="sleep"
                value={formData.sleep}
                onChange={handleChange}
                className="field-select"
              >
                <option value="">選択してください</option>
                <option value="excellent">よく眠れた</option>
                <option value="good">まあまあ眠れた</option>
                <option value="poor">眠れなかった</option>
                <option value="insomnia">ほとんど眠れなかった</option>
              </select>
            </div>

            <div className="form-field">
              <label className="field-label">気分</label>
              <select
                name="mood"
                value={formData.mood}
                onChange={handleChange}
                className="field-select"
              >
                <option value="">選択してください</option>
                <option value="excellent">とても良い</option>
                <option value="good">良い</option>
                <option value="normal">普通</option>
                <option value="low">落ち込み気味</option>
                <option value="depressed">憂うつ</option>
              </select>
            </div>
          </div>
        </section>

        {/* 自由記述 */}
        <section className="form-section">
          <h2 className="section-title">
            <span className="material-symbols-outlined">edit_note</span>
            その他（自由記述）
          </h2>
          <div className="form-field">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="field-textarea"
              rows={4}
              placeholder="気になる症状や変化、質問などがあればご記入ください"
            />
          </div>
        </section>

        {/* 送信ボタン */}
        <div className="form-actions">
          <Link to="/patient" className="cancel-button">
            キャンセル
          </Link>
          <button type="submit" className="submit-button">
            <span className="material-symbols-outlined">send</span>
            記録を送信
          </button>
        </div>
      </form>

      <style>{`
        .daily-record-page {
          max-width: 800px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          gap: var(--spacing-md);
          margin-bottom: var(--spacing-xl);
        }

        .back-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: var(--background-primary);
          color: var(--text-secondary);
          box-shadow: var(--shadow-sm);
        }

        .page-title {
          font-size: 1.5rem;
          font-weight: 600;
          flex: 1;
        }

        .page-date {
          font-size: 0.875rem;
          color: var(--text-secondary);
          background-color: var(--background-primary);
          padding: var(--spacing-xs) var(--spacing-md);
          border-radius: var(--border-radius-md);
        }

        .record-form {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-lg);
        }

        .form-section {
          background-color: var(--background-primary);
          padding: var(--spacing-lg);
          border-radius: var(--border-radius-lg);
          box-shadow: var(--shadow-sm);
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: var(--spacing-md);
          padding-bottom: var(--spacing-sm);
          border-bottom: 1px solid var(--border-color);
        }

        .section-title .material-symbols-outlined {
          color: #0891b2;
        }

        .condition-buttons {
          display: flex;
          gap: var(--spacing-sm);
          flex-wrap: wrap;
        }

        .condition-button {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--spacing-xs);
          padding: var(--spacing-md);
          border: 2px solid var(--border-color);
          border-radius: var(--border-radius-md);
          cursor: pointer;
          transition: all 0.2s;
          flex: 1;
          min-width: 80px;
        }

        .condition-button:hover {
          border-color: #0891b2;
        }

        .condition-button--selected {
          border-color: #0891b2;
          background-color: #f0fdfa;
        }

        .condition-button input {
          display: none;
        }

        .condition-emoji {
          font-size: 2rem;
        }

        .condition-label {
          font-size: 0.75rem;
          text-align: center;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--spacing-md);
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: var(--spacing-xs);
        }

        .field-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .field-select,
        .field-textarea {
          padding: var(--spacing-sm) var(--spacing-md);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          background-color: white;
        }

        .field-select:focus,
        .field-textarea:focus {
          outline: none;
          border-color: #0891b2;
          box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.1);
        }

        .field-textarea {
          resize: vertical;
        }

        .radio-group {
          display: flex;
          gap: var(--spacing-md);
          flex-wrap: wrap;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: var(--spacing-xs);
          cursor: pointer;
          font-size: 0.875rem;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: var(--spacing-md);
          padding: var(--spacing-lg) 0;
        }

        .cancel-button {
          padding: var(--spacing-sm) var(--spacing-lg);
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-md);
          background-color: white;
          color: var(--text-secondary);
          text-decoration: none;
          font-size: 0.875rem;
        }

        .submit-button {
          display: flex;
          align-items: center;
          gap: var(--spacing-sm);
          padding: var(--spacing-sm) var(--spacing-lg);
          background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
          color: white;
          border: none;
          border-radius: var(--border-radius-md);
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .submit-button:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-md);
        }

        @media (max-width: 768px) {
          .condition-buttons {
            flex-direction: column;
          }

          .condition-button {
            flex-direction: row;
            justify-content: flex-start;
            padding: var(--spacing-sm) var(--spacing-md);
          }

          .condition-emoji {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  )
}

